import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';

import NotoSansKRRegular from '@repo/common/assets/fonts/kr/NotoSansKR-Regular.ttf';
import NotoSansKRMedium from '@repo/common/assets/fonts/kr/NotoSansKR-Medium.ttf';
import MonaSansRegular from '@repo/common/assets/fonts/en/MonaSans-Regular.ttf';
import MonaSansMedium from '@repo/common/assets/fonts/en/MonaSans-Medium.ttf';

// 국문 문서라 한글 폰트를 기본으로 두고, 영문 글리프는 Mona Sans 로 보완한다.
Font.register({
  family: 'Certificate',
  fonts: [
    { src: NotoSansKRRegular, fontWeight: 'normal' },
    { src: NotoSansKRMedium, fontWeight: 500 },
  ],
});

Font.register({
  family: 'CertificateEn',
  fonts: [
    { src: MonaSansRegular, fontWeight: 'normal' },
    { src: MonaSansMedium, fontWeight: 500 },
  ],
});

// 표 안에서 긴 회사명/공고명이 줄바꿈되도록
Font.registerHyphenationCallback((word) => [word]);

const COLORS = {
  text: '#171410',
  sub: '#686561',
  line: '#D8D5D1',
  headBg: '#F5F3F1',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 44,
    paddingBottom: 56,
    paddingHorizontal: 44,
    fontFamily: 'Certificate',
    fontSize: 9.5,
    color: COLORS.text,
    backgroundColor: '#ffffff',
  },

  title: {
    fontSize: 20,
    fontWeight: 500,
    textAlign: 'center',
    letterSpacing: 6,
    marginBottom: 6,
  },
  titleEn: {
    fontFamily: 'CertificateEn',
    fontSize: 9,
    color: COLORS.sub,
    textAlign: 'center',
    marginBottom: 22,
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: 500,
    marginBottom: 8,
  },

  /* 인적사항 */
  infoTable: {
    borderTopWidth: 1,
    borderTopColor: COLORS.text,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
    marginBottom: 24,
  },
  infoRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
  },
  infoRowLast: {
    borderBottomWidth: 0,
  },
  infoLabel: {
    width: 78,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: COLORS.headBg,
    fontWeight: 500,
  },
  infoValue: {
    flex: 1,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },

  /* 지원내역 */
  summary: {
    fontSize: 9.5,
    color: COLORS.sub,
    marginBottom: 8,
  },
  table: {
    borderTopWidth: 1,
    borderTopColor: COLORS.text,
  },
  tr: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
  },
  th: {
    backgroundColor: COLORS.headBg,
    paddingVertical: 7,
    paddingHorizontal: 6,
    fontWeight: 500,
    textAlign: 'center',
  },
  td: {
    paddingVertical: 7,
    paddingHorizontal: 6,
  },
  colNo: { width: 28, textAlign: 'center' },
  colDate: { width: 66, textAlign: 'center' },
  colCompany: { width: 116 },
  colTitle: { flex: 1 },
  colLocation: { width: 96 },
  colStatus: { width: 52, textAlign: 'center' },

  empty: {
    paddingVertical: 28,
    textAlign: 'center',
    color: COLORS.sub,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
  },

  /* 확인 문구 · 발급 정보 */
  statement: {
    marginTop: 26,
    fontSize: 10,
    lineHeight: 1.6,
    textAlign: 'center',
  },
  issue: {
    marginTop: 26,
    alignItems: 'center',
  },
  issueDate: {
    fontSize: 11,
    marginBottom: 14,
  },
  issuer: {
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 1,
  },
  docNo: {
    marginTop: 8,
    fontSize: 8.5,
    color: COLORS.sub,
  },

  footer: {
    position: 'absolute',
    left: 44,
    right: 44,
    bottom: 24,
    fontSize: 7.5,
    color: COLORS.sub,
    textAlign: 'center',
  },
  pageNo: {
    position: 'absolute',
    right: 44,
    bottom: 24,
    fontSize: 7.5,
    color: COLORS.sub,
  },
});

// 조회 기간 라벨 (증명서 상단 요약에 표기)
const PERIOD_LABEL = {
  ALL: '전체 기간',
  '1M': '최근 1개월',
  '3M': '최근 3개월',
  '4M': '최근 4개월',
};

const STATUS_LABEL = {
  SUBMITTED: '제출',
  ACCEPTED: '승인',
  NEEDPREP: '반려',
};

const pad = (n) => String(n).padStart(2, '0');

/** 2026-09-11 (지원일 등 표 안에서 쓰는 짧은 형식) */
export const formatCertificateDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};

/** 2026년 9월 11일 (발급일 표기) */
export const formatKoreanDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
};

const dash = (value) => {
  const text = String(value ?? '').trim();
  return text || '-';
};

const InfoRow = ({ label, value, isLast }) => (
  <View style={[styles.infoRow, isLast && styles.infoRowLast]}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{dash(value)}</Text>
  </View>
);

/**
 * 구직활동 증명서 (국문).
 * 인적사항 + 전체 지원내역 표 + 확인 문구 + 발급 정보로 구성한다.
 *
 * @param {object} user      useAuth().user
 * @param {Array}  applies   MyJobApplyListResponse[] (최신순)
 * @param {string} period    조회 기간 (ALL | 1M | 3M | 4M)
 * @param {string} issuedAt  발급 기준 시각 (ISO). 생략 시 현재 시각
 * @param {string} documentNo 문서번호
 * @param {string} issuerName 발급 주체(회사명)
 */
const JobSeekingCertificatePDF = ({
  user,
  applies = [],
  period = 'ALL',
  issuedAt,
  documentNo,
  issuerName = '주식회사 오잉랩',
}) => {
  const fullName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const periodText = (() => {
    if (!applies.length) return '-';
    const dates = applies
      .map((item) => item?.createdAt)
      .filter(Boolean)
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a - b);
    if (!dates.length) return '-';
    const first = formatCertificateDate(dates[0]);
    const last = formatCertificateDate(dates[dates.length - 1]);
    return first === last ? first : `${first} ~ ${last}`;
  })();

  return (
    <Document
      title='구직활동 증명서'
      author={issuerName}
      subject='구직활동 증명서'
    >
      <Page size='A4' style={styles.page} wrap>
        <Text style={styles.title}>구직활동 증명서</Text>
        <Text style={styles.titleEn}>
          Certificate of Job-Seeking Activity
        </Text>

        {/* 인적사항 */}
        <Text style={styles.sectionTitle}>1. 인적사항</Text>
        <View style={styles.infoTable}>
          <InfoRow label='성명' value={fullName} />
          <InfoRow
            label='생년월일'
            value={formatCertificateDate(user?.birthday)}
          />
          <InfoRow label='국적' value={user?.nationality} />
          <InfoRow label='체류자격' value={user?.visaInfo?.type} />
          <InfoRow
            label='이메일'
            value={user?.email || user?.socialEmail}
            isLast
          />
        </View>

        {/* 지원내역 */}
        <Text style={styles.sectionTitle}>2. 구직활동(지원) 내역</Text>
        <Text style={styles.summary}>
          조회 기간 {PERIOD_LABEL[period] ?? PERIOD_LABEL.ALL} · 총{' '}
          {applies.length}건 · 활동기간 {periodText}
        </Text>

        <View style={styles.table}>
          <View style={styles.tr} fixed>
            <Text style={[styles.th, styles.colNo]}>번호</Text>
            <Text style={[styles.th, styles.colDate]}>지원일</Text>
            <Text style={[styles.th, styles.colCompany]}>회사명</Text>
            <Text style={[styles.th, styles.colTitle]}>지원 직무</Text>
            <Text style={[styles.th, styles.colLocation]}>근무지</Text>
            <Text style={[styles.th, styles.colStatus]}>상태</Text>
          </View>

          {applies.length === 0 ? (
            <Text style={styles.empty}>지원 내역이 없습니다.</Text>
          ) : (
            applies.map((item, index) => (
              <View
                key={item?.jobApplyId ?? `${index}`}
                style={styles.tr}
                wrap={false}
              >
                <Text style={[styles.td, styles.colNo]}>{index + 1}</Text>
                <Text style={[styles.td, styles.colDate]}>
                  {formatCertificateDate(item?.createdAt)}
                </Text>
                <Text style={[styles.td, styles.colCompany]}>
                  {dash(item?.companyName)}
                </Text>
                <Text style={[styles.td, styles.colTitle]}>
                  {dash(item?.title)}
                </Text>
                <Text style={[styles.td, styles.colLocation]}>
                  {dash(item?.companyAddress)}
                </Text>
                <Text style={[styles.td, styles.colStatus]}>
                  {STATUS_LABEL[item?.status] ?? dash(item?.status)}
                </Text>
              </View>
            ))
          )}
        </View>

        {/* 확인 문구 + 발급 정보는 한 페이지 안에 같이 나오도록 묶는다 */}
        <View wrap={false}>
          <Text style={styles.statement}>
            위와 같이 본인이 코레디(Koready) 채용 서비스를 통하여 구직활동을
            하였음을 증명합니다.
          </Text>

          <View style={styles.issue}>
            <Text style={styles.issueDate}>{formatKoreanDate(issuedAt)}</Text>
            <Text style={styles.issuer}>{issuerName}</Text>
            {documentNo ? (
              <Text style={styles.docNo}>문서번호 {documentNo}</Text>
            ) : null}
          </View>
        </View>

        <Text style={styles.footer} fixed>
          본 증명서는 코레디 서비스에 기록된 지원 내역을 기준으로 자동
          발급되었습니다.
        </Text>
        <Text
          style={styles.pageNo}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default JobSeekingCertificatePDF;
